import { useState, useEffect, useCallback } from "react";
import Layout from "../../components/Layout";
import Table from "react-bootstrap/Table";
import Spinner from "react-bootstrap/Spinner";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import {
	ORDER_STATUSES,
	getAdminOrders,
	formatCurrency,
	formatDateTime,
} from "../../api/orders";
import OrderDetailModal, { StatusBadge } from "./OrderDetailModal";
import "./style.css";

const PAGE_SIZES = [10, 20, 50, 100];

const Orders = () => {
	const [orders, setOrders] = useState([]);
	const [summary, setSummary] = useState({ count: 0, totalAmount: 0, totalTokens: 0 });
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(20);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [forbidden, setForbidden] = useState(false);
	const [selected, setSelected] = useState(null);

	// Filtreler
	const [q, setQ] = useState("");
	const [debouncedQ, setDebouncedQ] = useState("");
	const [status, setStatus] = useState("");
	const [from, setFrom] = useState("");
	const [to, setTo] = useState("");

	useEffect(() => {
		const t = setTimeout(() => setDebouncedQ(q.trim()), 400);
		return () => clearTimeout(t);
	}, [q]);

	// Filtre değişince 1. sayfaya dön
	useEffect(() => {
		setPage(1);
	}, [debouncedQ, status, from, to, limit]);

	const load = useCallback(async () => {
		setLoading(true);
		setError("");
		try {
			const data = await getAdminOrders({ page, limit, q: debouncedQ, status, from, to });
			setOrders(Array.isArray(data.orders) ? data.orders : []);
			setTotal(data.total || 0);
			setSummary(data.summary || { count: 0, totalAmount: 0, totalTokens: 0 });
			setForbidden(false);
		} catch (err) {
			if (err.status === 403) {
				setForbidden(true);
			} else {
				setError(err.message || "Siparişler alınamadı");
			}
			setOrders([]);
		} finally {
			setLoading(false);
		}
	}, [page, limit, debouncedQ, status, from, to]);

	useEffect(() => {
		load();
	}, [load]);

	const totalPages = Math.max(1, Math.ceil(total / limit));
	const currency = orders[0]?.currency || "TRY";

	const handleStatusChanged = (updated) => {
		setSelected(updated);
		load();
	};

	const clearFilters = () => {
		setQ("");
		setStatus("");
		setFrom("");
		setTo("");
	};

	const renderRows = () => {
		if (loading) {
			return (
				<tr>
					<td colSpan={11} className="text-center">
						<Spinner animation="border" variant="primary" />
					</td>
				</tr>
			);
		}
		if (error) {
			return (
				<tr>
					<td colSpan={11} className="text-center text-danger">
						{error}{" "}
						<Button variant="link" size="sm" onClick={load}>
							Tekrar dene
						</Button>
					</td>
				</tr>
			);
		}
		if (orders.length === 0) {
			return (
				<tr>
					<td colSpan={11} className="text-center">
						{debouncedQ || status || from || to
							? "Filtrelerle eşleşen sipariş bulunamadı."
							: "Henüz sipariş bulunmamaktadır."}
					</td>
				</tr>
			);
		}
		return orders.map((order, index) => {
			const cur = order.currency || "TRY";
			const buyer = order.buyer || {};
			const buyerName = [buyer.name, buyer.surname].filter(Boolean).join(" ");
			return (
				<tr key={order._id} className="order-row" onClick={() => setSelected(order)}>
					<td className="table-id">{(page - 1) * limit + index + 1}</td>
					<td className="mono">{order.orderNumber || "-"}</td>
					<td className="nowrap">{formatDateTime(order.paidAt)}</td>
					<td>
						<div className="order-customer">
							<strong>{order.username || "-"}</strong>
							{buyerName && <span>{buyerName}</span>}
							{buyer.email && <small>{buyer.email}</small>}
						</div>
					</td>
					<td>
						<ul className="order-items">
							{(order.items || []).map((item, i) => (
								<li key={item.id || i}>
									{item.name} × {item.quantity}
								</li>
							))}
						</ul>
					</td>
					<td className="num">{order.tokensPurchased ?? 0}</td>
					<td className="num nowrap">{formatCurrency(order.subtotal, cur)}</td>
					<td className="num nowrap">{formatCurrency(order.tax, cur)}</td>
					<td className="num nowrap">
						<strong>{formatCurrency(order.total, cur)}</strong>
					</td>
					<td className="nowrap">
						{order.cardAssociation || "Kart"}{" "}
						{order.cardLastFour ? `**** ${order.cardLastFour}` : ""}
					</td>
					<td>
						<StatusBadge status={order.status} />
						{order.shipment?.trackingNumber ? (
							<small className="order-ship-flag mono" title={order.shipment.carrier}>
								{order.shipment.carrier ? `${order.shipment.carrier} · ` : ""}
								{order.shipment.trackingNumber}
							</small>
						) : (
							order.requiresShipping && <small className="order-ship-flag">kargolu</small>
						)}
					</td>
				</tr>
			);
		});
	};

	if (forbidden) {
		return (
			<Layout>
				<div className="orders-wrapper">
					<div className="orders-forbidden">
						<h5>Bu sayfa için yetkiniz yok</h5>
						<p>Siparişleri yalnızca yönetici hesapları görüntüleyebilir.</p>
					</div>
				</div>
			</Layout>
		);
	}

	return (
		<Layout>
			<div className="orders-wrapper">
				<div className="order-summary-cards">
					<div className="order-summary-card">
						<span>Toplam Sipariş</span>
						<strong>{loading ? "…" : summary.count.toLocaleString("tr-TR")}</strong>
					</div>
					<div className="order-summary-card">
						<span>Toplam Tutar (KDV dahil)</span>
						<strong>{loading ? "…" : formatCurrency(summary.totalAmount, currency)}</strong>
					</div>
					<div className="order-summary-card">
						<span>Toplam Satılan Token</span>
						<strong>{loading ? "…" : (summary.totalTokens || 0).toLocaleString("tr-TR")}</strong>
					</div>
					<div className="order-summary-card highlight">
						<span>Bekleyen Kargo</span>
						<strong>
							{loading
								? "…"
								: (
										(summary.byStatus?.new || 0) +
										(summary.byStatus?.preparing || 0) +
										(summary.byStatus?.ready_to_ship || 0)
								  ).toLocaleString("tr-TR")}
						</strong>
						<small>
							Yeni {summary.byStatus?.new || 0} · Hazırlanıyor {summary.byStatus?.preparing || 0} ·
							Kargoya verilecek {summary.byStatus?.ready_to_ship || 0}
						</small>
					</div>
				</div>

				<div className="device-table-wrapper">
					<div className="order-filters">
						<Form.Control
							type="text"
							placeholder="Sipariş no, ödeme kimliği, kullanıcı, e-posta veya ad soyad ara..."
							value={q}
							onChange={(e) => setQ(e.target.value)}
							className="order-filter-search"
						/>
						<Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
							<option value="">Tüm Durumlar</option>
							{ORDER_STATUSES.map((s) => (
								<option value={s.key} key={s.key}>
									{s.label}
								</option>
							))}
						</Form.Select>
						<div className="order-filter-dates">
							<Form.Control
								type="date"
								value={from}
								max={to || undefined}
								onChange={(e) => setFrom(e.target.value)}
								title="Başlangıç tarihi"
							/>
							<span>–</span>
							<Form.Control
								type="date"
								value={to}
								min={from || undefined}
								onChange={(e) => setTo(e.target.value)}
								title="Bitiş tarihi"
							/>
						</div>
						{(q || status || from || to) && (
							<Button variant="outline-secondary" onClick={clearFilters}>
								Temizle
							</Button>
						)}
					</div>

					<Table striped bordered hover responsive className="alarm-table orders-table">
						<thead>
							<tr>
								<th></th>
								<th>Sipariş No</th>
								<th>Tarih</th>
								<th>Müşteri</th>
								<th>Kalemler</th>
								<th className="num">Token</th>
								<th className="num">Ara Toplam</th>
								<th className="num">KDV</th>
								<th className="num">Toplam</th>
								<th>Ödeme</th>
								<th>Durum</th>
							</tr>
						</thead>
						<tbody>{renderRows()}</tbody>
					</Table>

					<div className="order-pagination">
						<div className="order-pagination-info">
							Toplam <strong>{total.toLocaleString("tr-TR")}</strong> kayıt
							{total > 0 && (
								<>
									{" "}
									· {((page - 1) * limit + 1).toLocaleString("tr-TR")}–
									{Math.min(page * limit, total).toLocaleString("tr-TR")} arası
								</>
							)}
						</div>
						<div className="order-pagination-controls">
							<Form.Select
								size="sm"
								value={limit}
								onChange={(e) => setLimit(Number(e.target.value))}
							>
								{PAGE_SIZES.map((n) => (
									<option value={n} key={n}>
										{n} / sayfa
									</option>
								))}
							</Form.Select>
							<Button
								variant="outline-primary"
								size="sm"
								disabled={page <= 1 || loading}
								onClick={() => setPage((p) => p - 1)}
							>
								‹ Önceki
							</Button>
							<span>
								Sayfa {page} / {totalPages}
							</span>
							<Button
								variant="outline-primary"
								size="sm"
								disabled={page >= totalPages || loading}
								onClick={() => setPage((p) => p + 1)}
							>
								Sonraki ›
							</Button>
						</div>
					</div>
				</div>
			</div>

			<OrderDetailModal
				order={selected}
				onClose={() => setSelected(null)}
				onStatusChanged={handleStatusChanged}
			/>
		</Layout>
	);
};

export default Orders;
